import { FC, useMemo } from "react";
import { Box, Flex, Text, Badge } from "@chakra-ui/react";
import { EVENT_TYPES } from "../../enums/event";

/* ------------------------------------------------------------------ */
/*  Tipi                                                               */
/* ------------------------------------------------------------------ */

export type CalendarEventType =
  | "field"          // Allenamento campo
  | "workout-sheet"  // Scheda allenamento
  | "test"           // Test di valutazione
  | "match";         // Gara / partita

export interface CalendarEventCardProps {
  /** Titolo dell'evento */
  title: string;
  /** Tipo evento dal backend (EVENT_TYPES enum) */
  eventType: EVENT_TYPES;
  /** Sottotipo per workout: 'field' | 'workout-sheet' */
  workoutSubType?: "field" | "workout-sheet";
  /** Orario di inizio (formattato) */
  startTime?: string;
  /** Orario di fine (formattato) */
  endTime?: string;
  /** Nome del luogo / location */
  location?: string;
  /** Nome della scheda workout (se tipo scheda) */
  workoutName?: string;
  /** Numero di esercizi nella scheda */
  exerciseCount?: number;
  /** Durata stimata in minuti (per schede workout) */
  estimatedDuration?: number;
  /** Stato completamento per schede workout */
  completionStatus?: "pending" | "in-progress" | "completed";
  /** Click handler */
  onClick?: () => void;
  /** Extra badges da renderizzare accanto al tipo badge (ricorrente, completato, scheda, ecc.) */
  extraBadges?: React.ReactNode;
  /** Contenuto aggiuntivo (menu azioni, avatar, ecc.) */
  children?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Mappa colori bordo sinistro per tipo evento                        */
/* ------------------------------------------------------------------ */

const BORDER_COLORS: Record<CalendarEventType, string> = {
  field:          "gray.400",      // grigio → allenamento campo
  test:           "blue.500",      // blu → test
  match:          "red.500",       // rosso → gara
  "workout-sheet": "purple.500",   // colorato → scheda allenamento
};

const TYPE_LABELS: Record<CalendarEventType, string> = {
  field:          "Campo",
  test:           "Test",
  match:          "Gara",
  "workout-sheet": "Scheda",
};

const BADGE_COLOR_SCHEMES: Record<CalendarEventType, string> = {
  field:          "gray",
  test:           "blue",
  match:          "red",
  "workout-sheet": "purple",
};

/* ------------------------------------------------------------------ */
/*  Helper: risolvi il CalendarEventType effettivo                     */
/* ------------------------------------------------------------------ */

function resolveEventType(
  eventType: EVENT_TYPES,
  workoutSubType?: "field" | "workout-sheet",
): CalendarEventType {
  switch (eventType) {
    case EVENT_TYPES.TEST:
    case EVENT_TYPES.SYSTEM_TEST_EVENT:
      return "test";
    case EVENT_TYPES.MATCH:
      return "match";
    case EVENT_TYPES.WORKOUT:
    default:
      return workoutSubType === "workout-sheet" ? "workout-sheet" : "field";
  }
}

/* ------------------------------------------------------------------ */
/*  Completion badge per schede workout                                */
/* ------------------------------------------------------------------ */

const COMPLETION_LABELS: Record<string, { label: string; colorScheme: string }> = {
  pending:       { label: "Da fare",    colorScheme: "gray" },
  "in-progress": { label: "In corso",   colorScheme: "orange" },
  completed:     { label: "Completato", colorScheme: "green" },
};

/* ------------------------------------------------------------------ */
/*  Componente CalendarEventCard                                       */
/* ------------------------------------------------------------------ */

const CalendarEventCard: FC<CalendarEventCardProps> = ({
  title,
  eventType,
  workoutSubType,
  startTime,
  endTime,
  location,
  workoutName,
  exerciseCount,
  estimatedDuration,
  completionStatus,
  onClick,
  extraBadges,
  children,
}) => {
  const resolved = useMemo(
    () => resolveEventType(eventType, workoutSubType),
    [eventType, workoutSubType],
  );

  const borderColor = BORDER_COLORS[resolved];
  const typeLabel = TYPE_LABELS[resolved];
  const badgeScheme = BADGE_COLOR_SCHEMES[resolved];
  const isWorkoutSheet = resolved === "workout-sheet";

  return (
    <Box
      borderLeft="4px solid"
      borderLeftColor={borderColor}
      borderRadius="md"
      bg="white"
      px={3}
      py={2}
      shadow="sm"
      cursor={onClick ? "pointer" : "default"}
      _hover={onClick ? { shadow: "md", bg: "gray.50" } : undefined}
      transition="all 0.15s ease"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Header: badge tipo + extra badges + orario */}
      <Flex align="center" justify="space-between" mb={1}>
        <Flex align="center" gap={2} flex={1} flexWrap="wrap">
          <Badge colorScheme={badgeScheme} fontSize="xs" textTransform="uppercase">
            {typeLabel}
          </Badge>
          {extraBadges}
          <Text fontSize="sm" fontWeight="600" lineClamp={1}>
            {title}
          </Text>
        </Flex>

        {startTime && (
          <Text fontSize="xs" color="gray.500" flexShrink={0}>
            {startTime}
            {endTime ? ` – ${endTime}` : ""}
          </Text>
        )}
      </Flex>

      {/* Dettagli per scheda workout */}
      {isWorkoutSheet && (
        <Flex align="center" gap={3} mt={1} flexWrap="wrap">
          {workoutName && (
            <Text fontSize="xs" color="gray.600" lineClamp={1}>
              📋 {workoutName}
            </Text>
          )}
          {typeof exerciseCount === "number" && (
            <Text fontSize="xs" color="gray.500">
              {exerciseCount} esercizi
            </Text>
          )}
          {typeof estimatedDuration === "number" && (
            <Text fontSize="xs" color="gray.500">
              ~{estimatedDuration} min
            </Text>
          )}
          {completionStatus && COMPLETION_LABELS[completionStatus] && (
            <Badge
              colorScheme={COMPLETION_LABELS[completionStatus].colorScheme}
              fontSize="2xs"
              variant="subtle"
            >
              {COMPLETION_LABELS[completionStatus].label}
            </Badge>
          )}
        </Flex>
      )}

      {/* Location per eventi non-scheda */}
      {!isWorkoutSheet && location && (
        <Text fontSize="xs" color="gray.500" mt={0.5} lineClamp={1}>
          📍 {location}
        </Text>
      )}

      {/* Extra content (menu, avatars, etc.) */}
      {children}
    </Box>
  );
};

export default CalendarEventCard;
